// Ficha de Por pagar: cuánto, de quién, por qué y las acciones que le tocan.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-com-pagadas.html (panel "Por pagar")
import { Fragment, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import type { RetailCommission } from '@fiado/type-kit';
import { userMessageOf } from '@/api/errors';
import { Button, CopyableId, usePermissions } from '@/kit';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { StatusPill } from '@/shared/patterns/Pills';
import { useRetrySellerPayment } from '../../api/commissionQueries';
import { COMMISSION_PERMISSIONS } from '../../lib/commissionPermissions';
import { detailLines, type DueRow, type DueSection } from '../../lib/paymentRows';
import { SECTION_TITLE } from '../fieldStyles';
import { PaidOutsideDialog } from './PaidOutsideDialog';
import { ResolvePaymentDialog } from './ResolvePaymentDialog';

const HERO_LABEL: Record<DueSection, string> = {
    pending: 'Pendiente de depositar',
    review: 'Depósito sin confirmar',
    debts: 'Debe',
};

type Dialog = 'outside' | 'accredited' | 'failed' | null;

/** Con estos motivos el reintento no deposita: el pago sigue pendiente hasta resolverlos. */
const RETRY_STILL_PENDING: Partial<
    Record<`${RetailCommission.CommissionPaymentAttentionEnum}`, string>
> = {
    NO_WALLET: 'Sin wallet: el pago sigue pendiente hasta que se vincule su wallet.',
    PAUSED: 'Con los pagos de la cadena pausados, el pago sigue pendiente hasta reanudarlos.',
};

export function PaymentDueDetail({ row }: { row: DueRow }) {
    const { hasPermission } = usePermissions();
    const canManage = hasPermission(COMMISSION_PERMISSIONS.paymentManage);
    // Los avisos van en la mutación: corren aunque la ficha ya no esté.
    const retry = useRetrySellerPayment({
        onSuccess: () => {
            const blocked = row.balance?.attention ?? null;
            const pendingReason = blocked === null ? undefined : RETRY_STILL_PENDING[blocked];
            if (pendingReason === undefined)
                toast.success('Reintento en camino', {
                    description: 'El pago se vuelve a intentar con todo su pendiente.',
                });
            else toast.success('Reintento pedido', { description: pendingReason });
        },
        onError: (error) => {
            toast.error(userMessageOf(error));
        },
    });
    const [dialog, setDialog] = useState<Dialog>(null);
    const close = () => {
        setDialog(null);
    };

    const retryNow = () => {
        retry.mutate(row.sellerId);
    };

    return (
        <div className="flex h-full min-h-0 flex-col overflow-auto">
            <header className="flex flex-col gap-1 border-b border-border px-4 py-3">
                <span className="text-[9.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                    {HERO_LABEL[row.section]}
                    {row.since ? ` · desde ${formatDateTimeMx(row.since)}` : ''}
                </span>
                <p className="text-[24px] font-bold tabular-nums">
                    {formatMoneyCents(row.amountCents)}
                </p>
                <h2 className="truncate text-[13px] font-semibold">{row.sellerName}</h2>
                <div className="flex min-w-0 items-center gap-1 text-[10px] text-muted-foreground">
                    <span className="flex-none">{row.storeName} ·</span>
                    <CopyableId
                        inline
                        label="ID"
                        value={row.payment?.paymentId ?? row.sellerId}
                        className="min-w-0"
                    />
                </div>
                <div className="mt-1">
                    <StatusPill tone={row.tone}>{row.reason}</StatusPill>
                </div>
            </header>
            {canManage && row.section !== 'debts' && (
                <div className="flex flex-wrap gap-1.5 border-b border-border px-4 py-2.5">
                    {row.section === 'pending' ? (
                        <>
                            <Button
                                size="sm"
                                leftIcon={<RotateCcw className="size-3" aria-hidden />}
                                isLoading={retry.isPending}
                                onClick={retryNow}
                            >
                                Reintentar ahora
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                className="text-danger"
                                onClick={() => {
                                    setDialog('outside');
                                }}
                            >
                                Marcar pagado por fuera
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                size="sm"
                                onClick={() => {
                                    setDialog('accredited');
                                }}
                            >
                                Sí se depositó
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                className="text-danger"
                                onClick={() => {
                                    setDialog('failed');
                                }}
                            >
                                No se depositó
                            </Button>
                        </>
                    )}
                </div>
            )}
            <section className="px-4 py-3">
                <h3 className={SECTION_TITLE}>{row.payment ? 'Depósito' : 'Saldo'}</h3>
                <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-[11.5px]">
                    {detailLines(row).map((line) => (
                        <Fragment key={line.label}>
                            <dt className="text-muted-foreground">{line.label}</dt>
                            <dd className="text-right tabular-nums">{line.value}</dd>
                        </Fragment>
                    ))}
                </dl>
                {row.section === 'debts' && (
                    <p className="mt-2 text-[10.5px] text-muted-foreground">
                        Se descuenta de sus siguientes comisiones.
                    </p>
                )}
            </section>
            {dialog === 'outside' && (
                <PaidOutsideDialog
                    sellerId={row.sellerId}
                    sellerName={row.sellerName}
                    pendingCents={row.amountCents}
                    onClose={close}
                />
            )}
            {(dialog === 'accredited' || dialog === 'failed') && row.payment && (
                <ResolvePaymentDialog
                    payment={row.payment}
                    sellerName={row.sellerName}
                    accredited={dialog === 'accredited'}
                    onClose={close}
                />
            )}
        </div>
    );
}
